import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  apiClient,
  instancesAPI,
  documentsAPI,
  jobsAPI,
} from "../../../services/api";
import { useTranslation } from "react-i18next";

const Onboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUserProfile } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    bio: "",
    preferences: {
      goalRole: "",
      learningStyle: "",
      notifications: true,
      selectedBundles: [],
      trial_bundle: null,
      trial_instance_id: null,
    },
  });
  const [errors, setErrors] = useState({});
  const [demoProcessing, setDemoProcessing] = useState(false);
  const [demoSelected, setDemoSelected] = useState(false);
  const [uploadedDemoName, setUploadedDemoName] = useState("");
  const [demoInstanceId, setDemoInstanceId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);

  // Username validation state - only 'taken' used (checked on Next click)
  const [usernameStatus, setUsernameStatus] = useState(null); // null or 'taken'

  // Minimal bundles catalog (stubbed, no payments yet)
  const BUNDLES_BY_ROLE = {
    student: [
      {
        id: "math_pack",
        name: "Learn Mathematics",
        price: "€4.99",
        description: "Curated math books and exercises",
      },
      {
        id: "science_starter",
        name: "Science Starter",
        price: "€3.99",
        description: "Physics, chemistry, biology basics",
      },
      {
        id: "reading_club",
        name: "Reading Club",
        price: "€2.99",
        description: "Classics and modern literature",
      },
    ],
    teacher: [
      {
        id: "teacher_toolkit",
        name: "Teacher Toolkit",
        price: "€4.99",
        description: "Templates, rubrics, quick-start guides",
      },
      {
        id: "math_pack",
        name: "Learn Mathematics",
        price: "€4.99",
        description: "Curated math books and exercises",
      },
    ],
    professional: [
      {
        id: "career_elevate",
        name: "Career Elevate",
        price: "€5.99",
        description: "Communication, productivity, leadership",
      },
      {
        id: "recipes",
        name: "Recipes",
        price: "€2.99",
        description: "Healthy meals for busy schedules",
      },
    ],
    researcher: [
      {
        id: "research_reader",
        name: "Research Reader",
        price: "€3.99",
        description: "Papers, methods, note-taking patterns",
      },
      {
        id: "stat_methods",
        name: "Statistical Methods",
        price: "€4.99",
        description: "Stats, R, Python notebooks",
      },
    ],
  };

  // 🆕 Story 1.5.9: Removed Bundle Pack step (step 2), updated copy
  const steps = [
    {
      title: t('onboarding.welcome.title'),
      description: t('onboarding.welcome.desc'),
      component: WelcomeStep,
    },
    {
      title: t('onboarding.goal.title'),
      description: t('onboarding.goal.desc'),
      component: (props) => GoalStep({ ...props, t }),
    },
    // Bundle Pack step REMOVED per Story 1.5.9 requirements
    {
      title: t('onboarding.style.title'),
      description: t('onboarding.style.desc'),
      component: (props) => LearningStyleStep({ ...props, t }),
    },
    {
      title: "",
      description: "",
      // title: 'Upload Your First Document',
      // description: 'Upload a PDF to get started. Already have nothing to upload? Explore the built‑in demo instance anytime after onboarding.',
      component: (props) =>
        DemoStep({
          ...props,
          demoProcessing,
          setDemoProcessing,
          demoSelected,
          setDemoSelected,
          uploadedDemoName,
          setUploadedDemoName,
          onFileSelected: handleDemoFileSelected,
          jobProgress,
          jobStatus,
          t,
        }),
    },
    {
      title: t('onboarding.profile.title'),
      description: t('onboarding.profile.desc'),
      component: (props) => ProfileStep({ ...props, t }),
    },
    {
      title: t('onboarding.quick_wins.title'),
      description: t('onboarding.quick_wins.desc'),
      component: (props) => QuickWinsStep({ ...props, t }),
    },
  ];

  // WebSocket subscription for job updates (minimal)
  React.useEffect(() => {
    if (!jobId) return;
    let ws;
    try {
      ws = jobsAPI.createWebSocket(jobId, (data) => {
        if (typeof data?.progress === "number") setJobProgress(data.progress);
        if (data?.status) setJobStatus(data.status);
        if (data?.status === "completed" || data?.status === "failed") {
          setDemoProcessing(false);
        }
      });
    } catch (e) {
      // Silent fallback; we keep simple
    }
    return () => {
      try {
        ws && ws.close && ws.close();
      } catch {}
    };
  }, [jobId]);

  // Username validation moved to handleNext - no real-time checking to avoid keyboard collapse

  // Demo upload flow: create instance, upload file, auto-process, track job
  const handleDemoFileSelected = async (file) => {
    try {
      setUploadedDemoName(file.name);
      setDemoProcessing(true);

      alert(1);
      // Ensure an instance exists for this demo
      let instanceId = demoInstanceId;
      if (!instanceId) {
        try {
          const created = await instancesAPI.create("My First Document");
          instanceId =
            created?.id || created?.instance?.id || created?.instanceId;
        } catch (e) {
          // Fallback: use existing instance if limit reached
          const list = await instancesAPI.getAll();
          const first = list?.instances?.[0];
          instanceId = first?.id;
        }
        if (!instanceId) throw new Error("Failed to obtain instance");
        setDemoInstanceId(instanceId);
        // Persist for profile update
        setFormData((prev) => ({
          ...prev,
          preferences: {
            ...prev.preferences,
            trial_instance_id: instanceId,
          },
        }));
      }

      // Upload the file; API starts processing automatically
      const result = await documentsAPI.upload(instanceId, file, (pct) => {
        // Treat upload percent as job progress pre-processing
        setJobProgress(Math.min(pct, 95));
        setJobStatus("uploading");
      });

      const pjId = result?.processingJobId || result?.job_id || result?.jobId;
      console.log("Demo upload result:", result);
      console.log("Processing job ID:", pjId);
      if (pjId) {
        setJobId(pjId);
        setJobStatus("processing");
      } else {
        // No job id; keep simple spinner
        setJobStatus("processing");
      }
      setDemoSelected(true);
    } catch (e) {
      console.error("Demo upload failed:", e);
      setDemoProcessing(false);
    }
  };

  // 🆕 Story 1.5.9: Updated validation for removed Bundle Pack step
  const validateStep = (step) => {
    const stepErrors = {};

    switch (step) {
      case 0: // Welcome
        return true;
      case 1: // Goal setting
        if (!formData.preferences.goalRole) {
          stepErrors.goalRole = t('onboarding.goal.error');
        }
        break;
      case 2: // Learning style (optional)
        break;
      case 3: // Demo/Upload (optional)
        break;
      case 4: // Profile (REQUIRED: username only)
        if (!formData.username || !formData.username.trim()) {
          stepErrors.username = t('onboarding.profile.username_required');
        } else if (usernameStatus === "taken") {
          stepErrors.username = t('onboarding.profile.taken');
        }
        // Username availability checked in handleNext before proceeding
        break;
      case 5: // Quick Wins (optional)
        break;
      default:
        break;
    }

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      // Profile step (4): check username availability before proceeding
      if (currentStep === 4) {
        const username = formData.username?.trim();
        if (!username) {
          setErrors({ username: t('onboarding.profile.username_required') });
          return;
        }
        try {
          setLoading(true);
          const response = await apiClient.get(
            `/auth/check-username?username=${encodeURIComponent(username)}`
          );
          if (!response.data.available) {
            setUsernameStatus("taken");
            setErrors({ username: t('onboarding.profile.taken') });
            setLoading(false);
            return;
          }
          // Username available - clear any error and proceed
          setUsernameStatus(null);
          setErrors({});
          setLoading(false);
          setCurrentStep(currentStep + 1);
        } catch (error) {
          console.error("Username check failed:", error);
          setErrors({ username: t('onboarding.profile.error') });
          setLoading(false);
          return;
        }
      } else if (validateStep(currentStep)) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      await completeOnboarding();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      // Handle nested fields like preferences.learningStyle
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear errors for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      // Create user profile with onboarding data
      const profileData = {
        ...formData,
        onboarding_completed: true,
      };

      console.log("🎯 Completing onboarding with data:", profileData);

      const response = await apiClient.put("/auth/profile", profileData);

      if (response.data.success) {
        console.log("✅ Profile update successful:", response.data);

        // CRITICAL: Clear the onboarding flag BEFORE updating context
        // This ensures App.js won't show onboarding again
        try {
          localStorage.removeItem("show_onboarding_after_verification");
          console.log("🧹 Cleared onboarding flag from localStorage");
        } catch (e) {
          console.error("Failed to clear onboarding flag:", e);
        }

        // Update auth context with new profile
        updateUserProfile({
          ...response.data.profile,
          onboarding_completed: true,
        });

        console.log("👤 User profile updated in context");

        // Small delay to ensure state updates propagate
        await new Promise((resolve) => setTimeout(resolve, 100));

        console.log("🔄 Navigating to main feed...");
        navigate("/", { replace: true });
      } else {
        throw new Error(response.data.error || "Failed to complete onboarding");
      }
    } catch (error) {
      console.error("❌ Onboarding completion failed:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setErrors({ general: "Failed to complete setup. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Call the step component as a function instead of using JSX component syntax
  // This prevents React from treating each render's function as a "new component type"
  // which would unmount/remount the component and lose input focus
  const renderCurrentStep = () => {
    const StepComponent = steps[currentStep].component;
    const props = {
      formData,
      errors,
      onInputChange: handleInputChange,
      loading,
      usernameStatus,
      t,
    };
    // For function components wrapped in arrow functions, call directly
    // For plain component references (like WelcomeStep), use JSX
    if (typeof StepComponent === 'function' && StepComponent.prototype?.isReactComponent === undefined) {
      return StepComponent(props);
    }
    return <StepComponent {...props} />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">
              {t('onboarding.step_of', { current: currentStep + 1, total: steps.length })}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {steps[currentStep].title}
          </h1>
          <p className="text-gray-600">{steps[currentStep].description}</p>
        </div>

        {renderCurrentStep()}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.back')}
          </button>

          <button
            onClick={handleNext}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading && (
              <svg
                className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {currentStep === steps.length - 1 ? t('onboarding.get_started') : t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Step Components
const WelcomeStep = ({ t }) => (
  <div className="text-center">
    <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <svg
        className="w-12 h-12 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    </div>
    <p className="text-gray-600 mb-4">
      {t('onboarding.welcome.content')}
    </p>
  </div>
);

const GoalStep = ({ formData, errors, onInputChange, t }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 gap-3">
      {[
        { value: "student", label: t('onboarding.goal.options.student') },
        { value: "teacher", label: t('onboarding.goal.options.teacher') },
        { value: "professional", label: t('onboarding.goal.options.professional') },
        { value: "researcher", label: t('onboarding.goal.options.researcher') },
      ].map((opt) => (
        <label
          key={opt.value}
          className={`p-3 border rounded-md cursor-pointer ${
            formData.preferences.goalRole === opt.value
              ? "border-blue-600 bg-blue-50"
              : "border-gray-300 hover:bg-gray-50"
          }`}
        >
          <input
            type="radio"
            name="goalRole"
            value={opt.value}
            checked={formData.preferences.goalRole === opt.value}
            onChange={(e) =>
              onInputChange("preferences.goalRole", e.target.value)
            }
            className="mr-2"
          />
          {opt.label}
        </label>
      ))}
    </div>
    {errors.goalRole && (
      <p className="text-red-500 text-xs">{errors.goalRole}</p>
    )}
  </div>
);

const BundlesStep = ({ formData, onInputChange, role, catalog }) => {
  const roleKey = role || "student";
  const bundles = [
    {
      id: "free_trial",
      name: "Free Trial Bundle",
      price: "Free",
      description: "Get started with a preloaded book",
    },
    ...(catalog[roleKey] || []),
  ];
  const selected = new Set(formData.preferences.selectedBundles || []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onInputChange("preferences.selectedBundles", Array.from(next));
  };

  const fakeCheckout = async (bundle) => {
    try {
      // minimal stub: mark as selected after slight delay
      await new Promise((r) => setTimeout(r, 400));
      const next = new Set(formData.preferences.selectedBundles || []);
      next.add(bundle.id);
      onInputChange("preferences.selectedBundles", Array.from(next));
      // Mark trial bundle if free
      if (bundle.id === "free_trial") {
        onInputChange("preferences.trial_bundle", "free_trial");
      }
    } catch {}
  };

  return (
    <div className="space-y-3">
      {bundles.length === 0 && (
        <p className="text-gray-600 text-sm">
          No bundles for this role yet. You can add them later.
        </p>
      )}
      {bundles.map((b) => (
        <div
          key={b.id}
          className={`p-4 border rounded-md flex items-center justify-between ${
            selected.has(b.id)
              ? "border-blue-600 bg-blue-50"
              : "border-gray-300"
          }`}
        >
          <div>
            <div className="font-medium">
              {b.name}{" "}
              <span className="text-gray-500 font-normal">{b.price}</span>
            </div>
            <div className="text-sm text-gray-600">{b.description}</div>
          </div>
          <div className="flex items-center space-x-2">
            {!selected.has(b.id) && (
              <button
                type="button"
                onClick={() => fakeCheckout(b)}
                className="px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Buy
              </button>
            )}
            <button
              type="button"
              onClick={() => toggle(b.id)}
              className={`px-3 py-1 text-sm rounded-md ${
                selected.has(b.id)
                  ? "bg-blue-600 text-white"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {selected.has(b.id) ? "Selected" : "Select"}
            </button>
          </div>
        </div>
      ))}
      <div className="text-xs text-gray-500">
        You can buy these later. Selecting helps personalize your feed.
      </div>
    </div>
  );
};

const LearningStyleStep = ({ formData, errors, onInputChange, t }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        {t('onboarding.style.title')}
      </label>
      <div className="space-y-2">
        {[
          {
            value: "visual",
            label: t('onboarding.style.options.visual.label'),
            description: t('onboarding.style.options.visual.desc'),
          },
          {
            value: "auditory",
            label: t('onboarding.style.options.auditory.label'),
            description: t('onboarding.style.options.auditory.desc'),
          },
          {
            value: "kinesthetic",
            label: t('onboarding.style.options.kinesthetic.label'),
            description: t('onboarding.style.options.kinesthetic.desc'),
          },
          {
            value: "reading",
            label: t('onboarding.style.options.reading.label'),
            description: t('onboarding.style.options.reading.desc'),
          },
        ].map((style) => (
          <label
            key={style.value}
            className="flex items-start space-x-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50"
          >
            <input
              type="radio"
              name="learningStyle"
              value={style.value}
              checked={formData.preferences.learningStyle === style.value}
              onChange={(e) =>
                onInputChange("preferences.learningStyle", e.target.value)
              }
              className="mt-1"
            />
            <div>
              <div className="font-medium">{style.label}</div>
              <div className="text-sm text-gray-500">{style.description}</div>
            </div>
          </label>
        ))}
      </div>
      {errors.learningStyle && (
        <p className="text-red-500 text-xs mt-1">{errors.learningStyle}</p>
      )}
    </div>

    <div className="flex items-center justify-between">
      <label className="flex items-center space-x-3">
        <input
          type="checkbox"
          checked={formData.preferences.notifications}
          onChange={(e) =>
            onInputChange("preferences.notifications", e.target.checked)
          }
          className="h-4 w-4 text-blue-600 rounded"
        />
        <span className="text-sm text-gray-700">{t('onboarding.style.notifications')}</span>
      </label>
    </div>
  </div>
);

// 🆕 Story 1.5.9: Simplified Demo Step - focus on real upload, mention demo exploration after
const DemoStep = ({
  demoProcessing,
  setDemoProcessing,
  demoSelected,
  setDemoSelected,
  uploadedDemoName,
  setUploadedDemoName,
  onFileSelected,
  jobProgress,
  jobStatus,
  t,
}) => (
  // ... (keep commented code)
  <div className="space-y-6 text-center">
    <div className="relative inline-block">
<h2 className="text-3xl md:text-4xl font-bold text-gray-800 leading-snug">
  {t('onboarding.rapid_learning.title')}{" "}
  <span className="text-blue-600">{t('onboarding.rapid_learning.highlight')} </span>
  {t('onboarding.rapid_learning.with_ai')}
</h2>

      <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
    </div>
    <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
      {t('onboarding.rapid_learning.desc')}
    </p>
  </div>
);

const ProfileStep = ({ formData, errors, onInputChange, usernameStatus, t }) => (
  <div className="space-y-4">
    <div>
      <label
        htmlFor="username"
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {t('auth.username_label')} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          type="text"
          id="username"
          value={formData.username}
          onChange={(e) => onInputChange("username", e.target.value)}
          className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.username || usernameStatus === "taken"
              ? "border-red-500"
              : "border-gray-300"
          }`}
          placeholder={t('onboarding.profile.username_placeholder')}
        />
        {/* Visual feedback indicator - only show error state */}
        {usernameStatus === "taken" && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <svg
              className="h-5 w-5 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        )}
      </div>
      {errors.username && (
        <p className="text-red-500 text-xs mt-1">{errors.username}</p>
      )}
    </div>
  </div>
);

// (Removed old PreferencesStep; replaced with LearningStyleStep above)

const QuickWinsStep = ({ t }) => (
  <div className="space-y-3">
    <div className="flex items-center space-x-2">
      <span className="inline-block w-5 h-5 rounded-full bg-green-100 border border-green-300" />
      <span>{t('onboarding.quick_wins.upload')}</span>
    </div>
    <div className="flex items-center space-x-2">
      <span className="inline-block w-5 h-5 rounded-full bg-green-100 border border-green-300" />
      <span>{t('onboarding.quick_wins.view')}</span>
    </div>
    <div className="flex items-center space-x-2">
      <span className="inline-block w-5 h-5 rounded-full bg-green-100 border border-green-300" />
      <span>{t('onboarding.quick_wins.save')}</span>
    </div>
    <p className="text-xs text-gray-500">
      {t('onboarding.quick_wins.footer')}
    </p>
  </div>
);

export default Onboarding;
